import { hydra, rdf } from '@tpluscode/rdf-ns-builders';
import TermMap from '@rdf-esm/term-map';
import { namedNode } from '@rdf-esm/data-model';
import literal from 'rdf-literal';
import { createMixin } from "../Operation.mjs";

function only(termTypes = ['BlankNode', 'NamedNode', 'Literal']) {
  return function (term) {
    return termTypes.includes(term.term.termType);
  };
}

function getObject(obj) {
  if (obj.term.termType === 'BlankNode' || obj.term.termType === 'NamedNode') {
    return this._create(obj, [], {
      parent: this
    });
  }

  return literal.fromRdf(obj.term);
}

export function createHydraResourceMixin(alcaeus) {
  function* getSupportedClasses(resource) {
    for (const {
      root: docs
    } of alcaeus().apiDocumentations) {
      if (!docs) {
        continue;
      }

      const classes = docs.pointer.node(resource.out(rdf.type));

      for (const clas of classes.toArray()) {
        yield clas;
      }
    }
  }

  function HydraResourceMixin(base) {
    return class extends base {
      get operations() {
        const classOperations = [...getSupportedClasses(this.pointer)].reduce((operations, clas) => [...operations, ...clas.out(hydra.supportedOperation).toArray()], []);
        const propertyOperations = [...this.pointer.dataset.match(null, null, this.pointer.term)].reduce((operations, quad) => {
          if (quad.subject.termType !== 'NamedNode') {
            return operations;
          }

          return [...getSupportedClasses(this.pointer.namedNode(quad.subject))].reduce((operations, clas) => {
            return [...operations, ...clas.out(hydra.supportedProperty).has(hydra.property, quad.predicate).out(hydra.property).out(hydra.supportedOperation).toArray()];
          }, operations);
        }, []);
        const supportedOperations = Array.prototype.concat.apply([], [...classOperations, ...propertyOperations]);
        const operations = supportedOperations.reduce((map, pointer) => {
          if (!map.has(pointer.term)) {
            map.set(pointer.term, this._create(pointer, [createMixin(alcaeus(), this)]));
          }

          return map;
        }, new TermMap());
        return [...operations.values()];
      }

      get apiDocumentation() {
        var _client$resources$get;

        const client = alcaeus();
        const id = this.pointer.out(hydra.apiDocumentation).value || ((_client$resources$get = client.resources.get(this.pointer)) === null || _client$resources$get === void 0 ? void 0 : _client$resources$get.response.apiDocumentationLink);

        if (id) {
          const idNode = namedNode(id);
          const representation = client.apiDocumentations.find(apiDoc => {
            var _apiDoc$root;

            return (_apiDoc$root = apiDoc.root) === null || _apiDoc$root === void 0 ? void 0 : _apiDoc$root.equals(idNode);
          });

          if (representation !== null && representation !== void 0 && representation.root) {
            return representation.root;
          }
        }

        return undefined;
      }

      getLinks(includeMissing = false) {
        return this.getProperties({
          termTypes: ['NamedNode']
        }).filter(tuple => {
          var _tuple$supportedPrope;

          return (_tuple$supportedPrope = tuple.supportedProperty.property) === null || _tuple$supportedPrope === void 0 ? void 0 : _tuple$supportedPrope.isLink;
        }).filter(tuple => tuple.objects.length > 0 || includeMissing).map(tuple => ({
          resources: tuple.objects,
          supportedProperty: tuple.supportedProperty
        }));
      }

      getProperties(options) {
        const classProperties = [...getSupportedClasses(this.pointer)].reduce((operations, clas) => [...operations, ...clas.out(hydra.supportedProperty).toArray()], []);
        const map = classProperties.reduce((current, supportedProperty) => {
          const predicate = supportedProperty.out(hydra.property).toArray()[0];

          if (predicate.term.termType !== 'NamedNode' || current.has(predicate.term)) {
            return current;
          }

          const objects = this._getObjects(predicate.term).toArray().filter(only(options === null || options === void 0 ? void 0 : options.termTypes)).map(getObject, this);

          return current.set(predicate.term, {
            objects,
            supportedProperty: this._create(supportedProperty)
          });
        }, new TermMap());
        return [...map.values()];
      }

      getCollections(filter) {
        if (filter) {
          return this.collection.filter(c => {
            const memberAssertions = [...(c.memberAssertion || []), ...(c.manages || [])];
            return memberAssertions.find(assertion => assertion.matches(filter));
          });
        }

        return this.collection;
      }

    };
  }

  HydraResourceMixin.shouldApply = true;
  return HydraResourceMixin;
}